import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import {
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import * as React from 'react'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  isLoading?: boolean

  // Server-side pagination
  pageCount?: number
  pageIndex?: number
  pageSize?: number
  onPaginationChange?: (pagination: PaginationState) => void

  // Sorting
  sorting?: SortingState
  onSortingChange?: (sorting: SortingState) => void

  // Selection
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (selection: RowSelectionState) => void

  // Column visibility (controlled; falls back to internal state)
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (visibility: VisibilityState) => void

  // Row click
  onRowClick?: (row: TData) => void

  // Empty state
  emptyMessage?: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  pageCount,
  pageIndex = 0,
  pageSize = 20,
  onPaginationChange,
  sorting = [],
  onSortingChange,
  rowSelection = {},
  onRowSelectionChange,
  columnVisibility: controlledVisibility,
  onColumnVisibilityChange,
  onRowClick,
  emptyMessage = 'No results found.',
}: DataTableProps<TData, TValue>) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [internalVisibility, setInternalVisibility] = React.useState<VisibilityState>({})
  const columnVisibility = controlledVisibility ?? internalVisibility

  const isServerSide = pageCount !== undefined

  const table = useReactTable({
    data,
    columns,
    pageCount: isServerSide ? pageCount : undefined,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onSortingChange: onSortingChange
      ? (updater) => {
          const newSorting = typeof updater === 'function' ? updater(sorting) : updater
          onSortingChange(newSorting)
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnVisibility) : updater
      if (onColumnVisibilityChange) onColumnVisibilityChange(next)
      else setInternalVisibility(next)
    },
    onRowSelectionChange: onRowSelectionChange
      ? (updater) => {
          const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater
          onRowSelectionChange(newSelection)
        }
      : undefined,
    onPaginationChange: onPaginationChange
      ? (updater) => {
          const currentPagination = { pageIndex, pageSize }
          const newPagination = typeof updater === 'function' ? updater(currentPagination) : updater
          onPaginationChange(newPagination)
        }
      : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: isServerSide ? undefined : getFilteredRowModel(),
    getPaginationRowModel: isServerSide ? undefined : getPaginationRowModel(),
    getSortedRowModel: isServerSide ? undefined : getSortedRowModel(),
    manualPagination: isServerSide,
    manualSorting: isServerSide,
  })

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="w-full">
        <div className="overflow-hidden rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((_, index) => (
                  <TableHead key={index}>
                    <Skeleton className="h-4 w-20" />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      {/* Table with horizontal scroll on mobile */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(onRowClick && 'cursor-pointer')}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {isServerSide && pageCount && pageCount > 1 && (
        <DataTablePagination
          table={table}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
        />
      )}
    </div>
  )
}

interface DataTablePaginationProps<TData> {
  table: ReturnType<typeof useReactTable<TData>>
  pageCount: number
  pageIndex: number
  pageSize: number
}

function DataTablePagination<TData>({
  table,
  pageCount,
  pageIndex,
  pageSize,
}: DataTablePaginationProps<TData>) {
  const totalRows = pageCount * pageSize

  return (
    <div className="flex items-center justify-between py-4">
      <div className="text-[14px] text-text-secondary">
        {table.getFilteredSelectedRowModel().rows.length > 0 ? (
          <span>
            {table.getFilteredSelectedRowModel().rows.length} of {totalRows.toLocaleString()} row(s)
            selected.
          </span>
        ) : (
          <span>
            Page <span className="font-medium text-text-dark">{pageIndex + 1}</span> of{' '}
            <span className="font-medium text-text-dark">{pageCount}</span> (
            {totalRows.toLocaleString()} total)
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
          className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-card border border-border transition-all hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronsLeft className="w-[18px] h-[18px] text-foreground" />
        </button>
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border text-[14px] font-normal text-text-dark transition-all hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-[18px] h-[18px]" />
          Previous
        </button>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border text-[14px] font-normal text-text-dark transition-all hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
          <ChevronRight className="w-[18px] h-[18px]" />
        </button>
        <button
          onClick={() => table.setPageIndex(pageCount - 1)}
          disabled={!table.getCanNextPage()}
          className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-card border border-border transition-all hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronsRight className="w-[18px] h-[18px] text-foreground" />
        </button>
      </div>
    </div>
  )
}

export { DataTablePagination }
